import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Users, MousePointerClick, ArrowUpRight } from "lucide-react";
import { api, GROUP_SIZE } from "@/lib/api";
import { ShareButtons } from "@/components/ShareButtons";
import { NextMeetings } from "@/components/Meetings";

export const GroupCard = ({ number, members, isNext }) => (
  <div className="rounded-xl border border-[#D8D2C4] bg-[#FAF8F2] p-3" data-testid={`electorate-group-${number}`}>
    <div className="flex items-center justify-between mb-2">
      <span className="font-type font-bold text-sm text-[#1B2A41]">Group {number}</span>
      <span className={`font-type text-xs ${members.length >= GROUP_SIZE ? "text-[#E2705A]" : "text-[#5A7355]"}`}>
        {members.length}/{GROUP_SIZE}
        {members.length >= GROUP_SIZE ? " · Full" : isNext ? " · Open" : ""}
      </span>
    </div>
    <div className="h-1.5 rounded-full bg-[#D8D2C4] overflow-hidden mb-2">
      <div
        className="h-full bg-[#E2705A] transition-[width] duration-500"
        style={{ width: `${Math.min(100, (members.length / GROUP_SIZE) * 100)}%` }}
      />
    </div>
    <ol className="grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs text-[#1E2830]/85">
      {members.map((m, i) => (
        <li key={m.id} className="truncate">
          <span className="text-[#5C6A79] font-type mr-1">{i + 1}.</span>
          {m.name}
        </li>
      ))}
    </ol>
  </div>
);

export const ElectorateDetail = ({ name, onJoin }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["electorate", name],
    queryFn: () => api.get(`/electorates/${encodeURIComponent(name)}`).then((r) => r.data),
    enabled: !!name,
  });

  if (!name) {
    return (
      <div
        data-testid="electorate-detail-empty"
        className="h-full min-h-[220px] rounded-2xl border-2 border-dashed border-[#D8D2C4] flex flex-col items-center justify-center text-center p-6 text-[#5C6A79]"
      >
        <MousePointerClick className="w-8 h-8 mb-3 text-[#8FA58A]" />
        <p className="text-sm">Hover an electorate to see its numbers. Click one to view its F.E.R.M groups.</p>
      </div>
    );
  }

  const groups = {};
  (data?.members || []).forEach((m) => (groups[m.group_number] = [...(groups[m.group_number] || []), m]));
  const groupNums = Object.keys(groups).map(Number).sort((a, b) => a - b);
  if (data && !groupNums.includes(data.next_group)) groupNums.push(data.next_group);

  return (
    <div data-testid="electorate-detail-drawer" className="rounded-2xl bg-[#1B2A41] text-[#FDFBF7] p-5 sm:p-6 shadow-xl h-full">
      <p className="font-type text-[11px] uppercase tracking-[0.3em] text-[#8FA58A]">Federal Electorate</p>
      <h3 className="font-type font-bold text-2xl sm:text-3xl mt-1" data-testid="electorate-detail-name">
        {name}
      </h3>
      {isLoading || !data ? (
        <p className="mt-6 text-sm text-[#FDFBF7]/60">Loading...</p>
      ) : (
        <>
          <div className="flex flex-wrap gap-2 mt-3 text-xs">
            <span className="px-2.5 py-1 rounded-full bg-[#FDFBF7]/10">{data.state}</span>
            {data.enrollment && <span className="px-2.5 py-1 rounded-full bg-[#FDFBF7]/10">{data.enrollment.toLocaleString()} enrolled</span>}
            <span className="px-2.5 py-1 rounded-full bg-[#E2705A]/20 text-[#E2705A] font-semibold inline-flex items-center gap-1" data-testid="electorate-detail-count">
              <Users className="w-3 h-3" /> {data.count} F.E.R.M{data.count === 1 ? "" : "s"}
            </span>
          </div>
          {data.mp && (
            <p className="mt-3 text-xs text-[#FDFBF7]/70" data-testid="electorate-detail-mp">
              Sitting member: <span className="font-semibold text-[#FDFBF7]">{data.mp.name}</span> · {data.mp.party}
            </p>
          )}
          <div className="mt-5 space-y-3 max-h-[220px] overflow-y-auto pr-1">
            {groupNums.map((n) => (
              <GroupCard key={n} number={n} members={groups[n] || []} isNext={n === data.next_group} />
            ))}
          </div>
          <button
            type="button"
            onClick={() => onJoin(name)}
            data-testid="electorate-join-button"
            className="mt-5 w-full py-3 rounded-full bg-[#E2705A] hover:bg-[#D35D46] font-type font-bold uppercase tracking-wider text-sm transition-colors"
          >
            Join {name} · Group {data.next_group}
          </button>
          <Link
            to={`/electorate/${encodeURIComponent(name)}`}
            data-testid="electorate-open-page-link"
            className="mt-3 w-full py-2.5 rounded-full border border-[#FDFBF7]/25 hover:border-[#E2705A] hover:text-[#E2705A] text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors"
          >
            Meetings, bills &amp; votes <ArrowUpRight className="w-4 h-4" />
          </Link>
          <div className="mt-4">
            <NextMeetings state={data.state} electorate={name} dark />
          </div>
          <div className="mt-4 flex items-center justify-between gap-2">
            <span className="text-[11px] font-type uppercase tracking-wider text-[#8FA58A]">Share progress</span>
            <ShareButtons electorate={name} count={data.count} compact />
          </div>
          <p className="mt-3 text-[11px] text-[#FDFBF7]/50 leading-relaxed">
            Postcodes: {data.postcodes.slice(0, 8).join(", ")}
            {data.postcodes.length > 8 ? ` +${data.postcodes.length - 8} more` : ""}
          </p>
        </>
      )}
    </div>
  );
};
